import type Human from '@vladmandic/human';
import type {Config,FaceResult} from '@vladmandic/human';
export const FACE_CONFIG:Partial<Config>={
 backend:'webgl',modelBasePath:'/models/face/',cacheModels:true,debug:false,skipAllowed:false,
 face:{enabled:true,detector:{enabled:true,modelPath:'blazeface.json',maxDetected:2,minConfidence:.75,return:true,rotation:true},mesh:{enabled:true,modelPath:'facemesh.json'},description:{enabled:false},attention:{enabled:false},iris:{enabled:false},emotion:{enabled:false},antispoof:{enabled:false},liveness:{enabled:false},gear:{enabled:false}},
 body:{enabled:false},hand:{enabled:false},object:{enabled:false},segmentation:{enabled:false},gesture:{enabled:false},
};
type Graph={execute:(input:unknown,output:string)=>{data():Promise<ArrayLike<number>>;dispose():void}};
let loaded:Promise<{human:Human;graph:Graph}>|null=null;
let inference:Promise<unknown>=Promise.resolve();
async function models():Promise<{human:Human;graph:Graph}>{
 if(!loaded)loaded=(async()=>{const {default:HumanClass}=await import('@vladmandic/human');const human=new HumanClass(FACE_CONFIG);await human.load();const graph=await human.tf.loadGraphModel('/models/face/faceres.json');return{human,graph};})().catch(error=>{loaded=null;throw error;});
 return loaded;
}
export function validateFaceResults(faces:FaceResult[]):number[]{
 if(faces.length!==1)throw new Error('Debe aparecer exactamente una cara.');
 const embedding=faces[0]?.embedding;
 if(!embedding||embedding.length!==1024||embedding.some(v=>!Number.isFinite(v))||!embedding.some(v=>v!==0))throw new Error('No se pudo obtener una muestra facial válida.');
 return embedding;
}
async function sample(human:Human,graph:Graph,image:HTMLVideoElement|HTMLCanvasElement):Promise<{embedding:number[];box:number[]}>{
 const result=await human.detect(image);
 let input:{dispose():void}|null=null;let output:ReturnType<Graph['execute']>|null=null;
 try{
  if(result.face.length!==1)throw new Error('Debe aparecer exactamente una cara.');
  const face=result.face[0]!;
  if(face.boxScore<.75||!face.tensor)throw new Error('Acerca la cara y mejora la iluminación.');
  input=human.tf.tidy(()=>{const tensor=face.tensor!;const batched=tensor.rank===3?human.tf.expandDims(tensor,0):tensor;return human.tf.mul(human.tf.image.resizeBilinear(batched,[224,224]),255);});
  output=graph.execute(input,'global_pooling/Mean');
  const raw=Array.from(await output.data());
  const norm=Math.sqrt(raw.reduce((sum,value)=>sum+value*value,0));
  if(!Number.isFinite(norm)||norm<1e-12)throw new Error('No se pudo obtener una muestra facial válida.');
  const embedding=raw.map(value=>value/norm);
  validateFaceResults([{...face,embedding}]);
  return{embedding,box:Array.from(face.box)};
 }finally{output?.dispose();input?.dispose();for(const face of result.face)face.tensor?.dispose();}
}
export async function extractFaceEmbedding(human:Human,image:HTMLVideoElement|HTMLCanvasElement,graph:Graph):Promise<number[]>{return(await sample(human,graph,image)).embedding;}
export class FaceCaptureSession{
 private epoch=0;private stream:MediaStream|null=null;private ready:Awaited<ReturnType<typeof models>>|null=null;private busy=false;
 constructor(private readonly video:HTMLVideoElement){}
 async start():Promise<void>{
  if(this.stream)return;
  const epoch=++this.epoch;let stream:MediaStream|null=null;
  try{
   stream=await navigator.mediaDevices.getUserMedia({video:{width:{ideal:640},height:{ideal:480}},audio:false});
   if(epoch!==this.epoch){stream.getTracks().forEach(t=>t.stop());return;}
   this.stream=stream;this.video.muted=true;this.video.playsInline=true;this.video.srcObject=stream;this.video.hidden=false;await this.video.play();
   const ready=await models();if(epoch!==this.epoch)return;this.ready=ready;
  }catch(error){if(epoch===this.epoch)await this.stop();else stream?.getTracks().forEach(t=>t.stop());throw error;}
 }
 async sample():Promise<number[]>{
  if(this.busy||!this.ready||!this.stream||this.video.readyState<2)throw new Error('La cámara aún no está preparada.');
  const epoch=this.epoch;const ready=this.ready;this.busy=true;
  const execute=async()=>{
   if(epoch!==this.epoch)throw new Error('Captura cancelada.');
   const a=await sample(ready.human,ready.graph,this.video);
   if(epoch!==this.epoch)throw new Error('Captura cancelada.');
   const b=await sample(ready.human,ready.graph,this.video);
   if(epoch!==this.epoch)throw new Error('Captura cancelada.');
   const scale=Math.max(a.box[2]??0,a.box[3]??0,1);
   if(a.box.some((value,index)=>Math.abs(value-(b.box[index]??value))>.12*scale))throw new Error('Mantén la cara quieta y vuelve a capturar.');
   return b.embedding;
  };
  const next=inference.then(execute,execute);inference=next.catch(()=>undefined);
  try{return await next;}finally{if(epoch===this.epoch)this.busy=false;}
 }
 async stop():Promise<void>{++this.epoch;this.stream?.getTracks().forEach(t=>t.stop());this.stream=null;this.ready=null;this.busy=false;this.video.srcObject=null;this.video.hidden=true;}
}
